import { format, parseISO } from 'date-fns';
import type { TimelineEvent } from '../types';

interface Props {
  event: TimelineEvent;
  onClose: () => void;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function EventDetail({ event, onClose }: Props) {
  return (
    <aside style={styles.panel}>
      <div style={styles.header}>
        <h2 style={styles.title}>{event.title}</h2>
        <button style={styles.closeBtn} onClick={onClose}>✕</button>
      </div>

      <div style={styles.body}>
        {/* Type badge */}
        <span style={{ ...styles.typeBadge, background: event.color }}>
          {event.type}
        </span>

        <dl style={styles.dl}>
          <dt>Time</dt>
          <dd>{format(parseISO(event.timestamp), 'PPpp')}</dd>

          <dt>Mailbox</dt>
          <dd>{event.mailbox}</dd>

          <dt>From</dt>
          <dd>{event.actor}</dd>

          {event.metadata?.sensitivityLabel && (
            <>
              <dt>Sensitivity</dt>
              <dd>{event.metadata.sensitivityLabel}</dd>
            </>
          )}

          <dt>Conversation</dt>
          <dd style={styles.mono}>{event.conversationId}</dd>

          <dt>Message ID</dt>
          <dd style={styles.mono}>{event.metadata?.messageId}</dd>

          {event.metadata?.previousFolder && (
            <>
              <dt>Moved from</dt>
              <dd>{event.metadata.previousFolder} → {event.metadata.newFolder}</dd>
            </>
          )}
        </dl>

        {/* Description / snippet */}
        {event.description && (
          <div style={styles.section}>
            <div style={styles.sectionLabel}>Preview</div>
            <p style={styles.preview}>{event.description}</p>
          </div>
        )}

        {/* Attachments */}
        {event.metadata?.attachments && event.metadata.attachments.length > 0 && (
          <div style={styles.section}>
            <div style={styles.sectionLabel}>Attachments</div>
            {event.metadata.attachments.map((a, i) => (
              <div key={i} style={styles.attachment}>
                <span>📎 {a.name}</span>
                <span style={{ color: '#6b7280', fontSize: 12 }}>
                  {a.contentType} · {formatBytes(a.size)}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Open in Outlook */}
        {event.metadata?.linkToMessage && (
          <a
            href={event.metadata.linkToMessage}
            target="_blank"
            rel="noreferrer"
            style={styles.outlookLink}
          >
            Open in Outlook Web →
          </a>
        )}

        {/* Source header */}
        {event.metadata?.sourceHeader && (
          <div style={styles.section}>
            <div style={styles.sectionLabel}>Source Header</div>
            <pre style={styles.pre}>{event.metadata.sourceHeader}</pre>
          </div>
        )}
      </div>
    </aside>
  );
}

const styles: Record<string, React.CSSProperties> = {
  panel: {
    width: 340, minWidth: 280, background: '#fff',
    borderLeft: '1px solid #e5e7eb', display: 'flex',
    flexDirection: 'column', overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    padding: '16px 16px 12px', borderBottom: '1px solid #e5e7eb',
  },
  title: { fontSize: 15, fontWeight: 700, margin: 0, flex: 1, lineHeight: 1.3 },
  closeBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 18, color: '#6b7280', marginLeft: 8, padding: 0,
  },
  body: { flex: 1, overflowY: 'auto', padding: 16 },
  typeBadge: {
    display: 'inline-block', padding: '3px 10px', borderRadius: 12,
    color: '#fff', fontSize: 12, fontWeight: 600, textTransform: 'capitalize', marginBottom: 12,
  },
  dl: { display: 'grid', gridTemplateColumns: '100px 1fr', gap: '6px 8px', fontSize: 13, margin: '0 0 12px' },
  mono: { fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' },
  section: { marginBottom: 12 },
  sectionLabel: { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 },
  preview: { fontSize: 13, color: '#374151', background: '#f9fafb', padding: 8, borderRadius: 6, margin: 0 },
  attachment: {
    display: 'flex', justifyContent: 'space-between', fontSize: 13,
    padding: '4px 0', borderBottom: '1px solid #f3f4f6',
  },
  outlookLink: {
    display: 'block', marginTop: 12, color: '#2563eb',
    fontSize: 13, textDecoration: 'none', fontWeight: 600,
  },
  pre: {
    fontSize: 11, background: '#f9fafb', padding: 8,
    borderRadius: 4, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
  },
};
